import includes from 'lodash/includes'
import filter from 'lodash/filter'
import {PATHS} from '@frontrow/console/src/entities/helpers/paths'
import {
  type ImgixSizeParamsT,
  getUrlWithImgixParams
} from '@frontrow/console/src/entities/types/imgix'
import {type FancyOk, type fancyOperations} from '@frontrow/console/src/open-api/clients'

const MATURE_IMAGES_MIN_GRADE = 6

type PoseT = 'normal'

/** `getItemsR` and `postItemsR` both return this object; it needs no conversion. */
export type ItemT = FancyOk<fancyOperations['getItemsR']>[number]

export type StoreCategoryT = ItemT['storeCategory']

// Mirrors `SchoolGrade`'s codec (backend/core/library/Freckle/Core/SchoolGrade.hs),
// where "K" and "0" are both kindergarten.
export function gradeToNumber(grade: ItemT['minGrade']): number {
  if (typeof grade === 'number') {
    return grade
  }
  return grade === 'K' ? 0 : parseInt(grade, 10)
}

type ItemNameParamsT = {
  itemCategory: string
  avatarName: string
  itemName: string
  avatarPose: PoseT
  itemVariationName: string | undefined | null
}

function getItemImageName({
  itemCategory,
  avatarName,
  itemName,
  avatarPose,
  itemVariationName
}: ItemNameParamsT): string {
  switch (itemCategory) {
    case 'avatar':
      return itemVariationName !== null && itemVariationName !== undefined
        ? `${itemName}-${avatarPose}-${itemVariationName}`
        : `${itemName}-${avatarPose}`

    case 'background':
      return itemName

    default:
      return itemVariationName !== null && itemVariationName !== undefined
        ? `${avatarName}-${avatarPose}-${itemName}-${itemVariationName}`
        : `${avatarName}-${avatarPose}-${itemName}`
  }
}

type UrlConfigT = {
  itemName: string
  itemCategory: string
  avatarName: string
  avatarPose: PoseT
  studentGrade: number
  trimmedImage: boolean
  itemVariationName: string | undefined | null
  sizeParams?: ImgixSizeParamsT | null
}

export function getItemUrl(itemConfig: UrlConfigT): string {
  return getItemUrlAndName(itemConfig).itemUrl
}

//We don't support variations for grades below MATURE_IMAGES_MIN_GRADE
function canViewItemVariations(studentGrade: number): boolean {
  return studentGrade >= MATURE_IMAGES_MIN_GRADE
}

function getItemUrlAndName(itemConfig: UrlConfigT): {
  itemUrl: string
  itemImageName: string
} {
  const {trimmedImage, studentGrade, sizeParams, ...itemImageInfo} = itemConfig

  const updatedImageInfo = canViewItemVariations(studentGrade)
    ? itemImageInfo
    : {...itemImageInfo, itemVariationName: null}

  const itemSlug = getItemPathSlug(trimmedImage, studentGrade)
  const baseUrl = `${PATHS.imageAssetsUrl}/${itemSlug}/`
  const itemImageName = getItemImageName(updatedImageInfo)

  return {
    itemUrl: getUrlWithImgixParams(`${baseUrl}${itemImageName}.png`, sizeParams),
    itemImageName
  }
}

function getItemPathSlug(trimmed: boolean, grade: number): string {
  const itemSlug = trimmed ? 'items-trimmed' : 'items'
  const itemSlugAppend = canViewItemVariations(grade) ? '-mature' : ''

  return `${itemSlug}${itemSlugAppend}`
}

/** @internal */
export function filterItemsByStudentGrade(
  items: Array<ItemT>,
  grade: number,
  itemOwnershipIds: Array<number>
): Array<ItemT> {
  return filter(
    items,
    x =>
      includes(itemOwnershipIds, x.id) ||
      (gradeToNumber(x.minGrade) <= grade && grade <= gradeToNumber(x.maxGrade))
  )
}
